import React from 'react';
import { TrendingDown, TrendingUp, Minus, Activity } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';

interface AverageShrinkageProps {
  value: number;
  trend: 'up' | 'down' | 'stable';
  loading?: boolean;
  isFetching?: boolean;
}

export const AverageShrinkage: React.FC<AverageShrinkageProps> = ({ value, trend, loading = false, isFetching }) => {
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="bg-white rounded-[24px] border border-gray-100 p-4 h-full animate-pulse flex flex-col justify-between min-h-[120px]">
        <div className="h-7 w-7 rounded-lg bg-gray-100" />
        <div className="space-y-2">
          <div className="h-3 w-20 bg-gray-100 rounded" />
          <div className="h-6 w-16 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  const trendIcon = trend === 'up'
    ? <TrendingUp className="h-3.5 w-3.5 text-rose-500" />
    : trend === 'down'
      ? <TrendingDown className="h-3.5 w-3.5 text-emerald-500" />
      : <Minus className="h-3.5 w-3.5 text-gray-400" />;

  const trendColor = trend === 'up' ? 'text-rose-600' : trend === 'down' ? 'text-emerald-600' : 'text-gray-500';

  const trendText = trend === 'up'
    ? t('dashboard.averageShrinkage.trendIncreased')
    : trend === 'down'
      ? t('dashboard.averageShrinkage.trendDecreased')
      : t('dashboard.averageShrinkage.trendStable');

  return (
    <div
      className={`bg-white rounded-[24px] border border-gray-100 p-4 h-full flex flex-col justify-between min-h-[120px] transition-opacity duration-200 ${isFetching ? 'opacity-60' : 'opacity-100'}`}
    >
      <div className="p-2 rounded-xl bg-violet-50 text-violet-600 w-fit">
        <Activity className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 truncate">
          {t('dashboard.averageShrinkage.title')}
        </p>
        <p className="text-2xl font-bold text-gray-900 tracking-tight mt-1">
          {value.toFixed(2)}%
        </p>
        <div className={`mt-1.5 flex items-center gap-1 text-[11px] ${trendColor}`}>
          {trendIcon}
          <span className="font-medium truncate">{trendText}</span>
        </div>
      </div>
    </div>
  );
};
